import React from 'react';

import { Badge, Box, Card, Center, Flex, Text, useMantineTheme } from '@mantine/core';

import { formatCoachLabel, formatEventTime, resolveCardVisuals } from './resolveCardVisuals';
import WorkoutVisibilityIcon from '../../WorkoutVisibilityIcon';
import type { AthleteCalendarItem } from '../types';

const ICON_TILE_SIZE = 36;

interface DayCardProps {
  item: AthleteCalendarItem;
  onClick: (item: AthleteCalendarItem) => void;
  menu?: React.ReactNode;
}

const DayCard = ({ item, onClick, menu }: DayCardProps): JSX.Element => {
  const theme = useMantineTheme();
  const { Icon, accent, tint, fill, foreground, isCompleted, isHidden, typeLabel, subtitle } =
    resolveCardVisuals(item, theme);
  const coachLabel = item.kind === 'workout' ? null : formatCoachLabel(item.coachName);

  return (
    <Card
      p='sm'
      radius='md'
      data-cy={`athleteCalendarCard-${item.kind}`}
      data-source={item.kind === 'workout' ? item.source : undefined}
      onClick={() => onClick(item)}
      sx={{
        backgroundColor: fill ?? theme.white,
        color: foreground,
        border: fill ? 'none' : `1px solid ${theme.colors.gray[3]}`,
        borderLeft: `4px solid ${accent}`,
        cursor: 'pointer',
        transition: 'box-shadow 150ms ease',
        '&:hover': { boxShadow: theme.shadows.sm },
      }}
    >
      <Flex gap='sm' align={item.kind === 'workout' ? 'center' : 'flex-start'}>
        {item.kind === 'workout' ? (
          <Center
            sx={{
              width: ICON_TILE_SIZE,
              height: ICON_TILE_SIZE,
              flexShrink: 0,
              borderRadius: theme.radius.sm,
              backgroundColor: tint,
            }}
          >
            <Icon size={20} color={accent} />
          </Center>
        ) : (
          <Icon size={20} color={foreground} style={{ flexShrink: 0, marginTop: 2 }} />
        )}

        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Flex gap={8} align='center' wrap='wrap'>
            <Text fw={700} size='sm' sx={{ color: foreground }}>
              {item.name}
            </Text>
            {isCompleted && (
              <Badge
                color='green'
                variant='filled'
                radius='sm'
                size='sm'
                sx={{ flexShrink: 0, textTransform: 'none' }}
              >
                Completed
              </Badge>
            )}
            {isHidden && <WorkoutVisibilityIcon visible={false} withinPortal />}
          </Flex>

          {subtitle && (
            <Text size='xs' mt={2} sx={{ color: theme.colors.gray[6] }} data-cy='athleteCalendarCardClass'>
              {subtitle}
            </Text>
          )}

          {item.kind !== 'workout' && (
            <>
              <Text size='xs' fw={600} mt={2} sx={{ color: foreground, opacity: 0.9 }}>
                {formatEventTime(item.startTime)}
                {coachLabel && ` · ${coachLabel}`}
              </Text>
              <Badge
                radius='sm'
                size='sm'
                mt={8}
                sx={{
                  flexShrink: 0,
                  textTransform: 'none',
                  backgroundColor: foreground,
                  color: fill ?? theme.white,
                }}
              >
                {typeLabel}
              </Badge>
            </>
          )}
        </Box>

        {menu}
      </Flex>
    </Card>
  );
};

export default DayCard;
